import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useWorkspaceLoader } from '@app/components/Risk/Workspaces/hooks/useWorkspaceLoader'
import {
  activeCapitalSetIndexAtom,
  activeScenarioSetIndexAtom,
  activeWorkspaceAtom,
  getCapitalSetOptions,
  getScenarioSetOptions,
  type WorkspaceSetOption,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { useAtomValue } from 'jotai'

type WorkspaceSetSelectProps = {
  label: string
  options: WorkspaceSetOption[]
  value: number | null
  onChange: (index: number) => void
  disabled: boolean
}

const WorkspaceSetSelect = ({
  label,
  options,
  value,
  onChange,
  disabled,
}: WorkspaceSetSelectProps) => (
  <div className="min-w-0 flex-1">
    <span className="block px-1 pb-[2px] text-xs font-medium text-muted-foreground">{label}</span>
    <Select
      value={value === null ? '' : String(value)}
      onValueChange={(index) => onChange(Number(index))}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={label}
        className="h-9 bg-background text-xs data-[state=open]:border-rexBlue-500 data-[state=open]:ring-2 data-[state=open]:ring-rexBlue-500/30"
      >
        <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {options.map(({ index, name }) => (
          <SelectItem key={index} value={String(index)} className="text-xs">
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
)

export const WorkspaceSetSelects = () => {
  const activeWorkspace = useAtomValue(activeWorkspaceAtom)
  const scenarioSetIndex = useAtomValue(activeScenarioSetIndexAtom)
  const capitalSetIndex = useAtomValue(activeCapitalSetIndexAtom)
  const { switchWorkspaceSet } = useWorkspaceLoader()

  const scenarioSetOptions = getScenarioSetOptions(activeWorkspace)
  const capitalSetOptions = getCapitalSetOptions(activeWorkspace)

  return (
    <div className="flex w-full gap-2 mt-2 mb-1">
      <WorkspaceSetSelect
        label="Scenario"
        options={scenarioSetOptions}
        value={scenarioSetIndex}
        onChange={(index) => switchWorkspaceSet('scenario', index)}
        disabled={!activeWorkspace || scenarioSetOptions.length === 0}
      />
      <WorkspaceSetSelect
        label="Capital"
        options={capitalSetOptions}
        value={capitalSetIndex}
        onChange={(index) => switchWorkspaceSet('capital', index)}
        disabled={!activeWorkspace || capitalSetOptions.length === 0}
      />
    </div>
  )
}
